import { getScrollParents } from '@react-aria/utils';

// Дополнительный отступ элемента, который учитывается при подскролле к элементу
const ELEMENT_PADDING = 40;

// Большая часть кода взята из @react-aria/utils

/**
 * Вычисляет offset left или top от child до ancestor,
 * накапливая offsetLeft или offsetTop с помощью обхода всех offsetParent
 */
function relativeOffset(ancestor: HTMLElement, child: HTMLElement, axis: 'left' | 'top') {
    const prop = axis === 'left' ? 'offsetLeft' : 'offsetTop';
    let sum = 0;
    while (child.offsetParent) {
        sum += child[prop];
        if (child.offsetParent === ancestor) {
            // Останавливаемся, как только мы найдем интересующего нас предка.
            break;
        } else if (child.offsetParent.contains(ancestor)) {
            // Если предок не имеет `position:relative`, то мы останавливаемся на
            // его родителе с position:relative и вычитаем его смещение, так что
            // в итоге получаем правильное смещение от потомка до предка.
            sum -= ancestor[prop];
            break;
        }
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- автоматический игнор в рамках DIRECT-224975
        child = child.offsetParent as HTMLElement;
    }
    return sum;
}

interface ScrollIntoViewportOpts {
    /** Необязательный содержащий target элемент контейнер, который будет центрирован в viewport. */
    containingElement?: Element | null;
    /** Поведение прокрутки: 'smooth' для плавной анимации, 'auto' или 'instant' для мгновенной */
    behavior?: ScrollBehavior;
}

/**
 * Скроллит контейнер `scrollView` так что `element` становится видимым, с доп паддингом.
 * Ведет себя аналогично `element.scrollIntoView({block: 'nearest'})`, но с доп паддингом,
 * Но не влияет на контейнеры выше по дереву `scrollView`.
 */
export function scrollIntoView(scrollView: HTMLElement, element: HTMLElement, behavior?: ScrollBehavior): void {
    const offsetX = relativeOffset(scrollView, element, 'left');
    const offsetY = relativeOffset(scrollView, element, 'top');
    const width = element.offsetWidth;
    const height = element.offsetHeight;
    let x = scrollView.scrollLeft;
    let y = scrollView.scrollTop;

    const {
        borderTopWidth,
        borderLeftWidth,
        scrollPaddingTop,
        scrollPaddingRight,
        scrollPaddingBottom,
        scrollPaddingLeft,
    } = getComputedStyle(scrollView);

    const borderAdjustedX = x + parseInt(borderLeftWidth, 10);
    const borderAdjustedY = y + parseInt(borderTopWidth, 10);

    // Игнорируем end/bottom border с помощью clientHeight/Width вместо offsetHeight/Width
    const maxX = borderAdjustedX + scrollView.clientWidth;
    const maxY = borderAdjustedY + scrollView.clientHeight;

    // Получаем значения scroll values в px - или возвращаем 0 если не используется scroll-padding
    const scrollPaddingTopNumber = parseInt(scrollPaddingTop, 10) || 0;
    const scrollPaddingBottomNumber = parseInt(scrollPaddingBottom, 10) || 0;
    const scrollPaddingRightNumber = parseInt(scrollPaddingRight, 10) || 0;
    const scrollPaddingLeftNumber = parseInt(scrollPaddingLeft, 10) || 0;

    // Если положение element находится до текущей позиции скролла, то подскралливаем влево с паддингом
    if (offsetX - ELEMENT_PADDING <= x + scrollPaddingLeftNumber) {
        x = offsetX - ELEMENT_PADDING - parseInt(borderLeftWidth, 10) - scrollPaddingLeftNumber;
    } else if (offsetX + width + ELEMENT_PADDING > maxX - scrollPaddingRightNumber) {
        // Если положение element находится после текущей позиции скролла с шириной контейнера,
        // то подскралливаем вправо с паддингом
        x += offsetX + width + ELEMENT_PADDING - maxX + scrollPaddingRightNumber;
    }
    // Если положение element находится до текущей позиции скролла, то подскралливаем вверх с паддингом
    if (offsetY - ELEMENT_PADDING <= borderAdjustedY + scrollPaddingTopNumber) {
        y = offsetY - ELEMENT_PADDING - parseInt(borderTopWidth, 10) - scrollPaddingTopNumber;
    } else if (offsetY + height + ELEMENT_PADDING > maxY - scrollPaddingBottomNumber) {
        // Если положение element находится после текущей позиции скролла с высотой контейнера,
        // то подскралливаем вниз с паддингом
        y += offsetY + height + ELEMENT_PADDING - maxY + scrollPaddingBottomNumber;
    }

    scrollView.scrollTo({ left: x, top: y, behavior });
}

/**
 * Прокручивает targetElement так, чтобы он был виден во viewport. Принимает опциональный opts.containingElement,
 * который будет отцентрирован во viewport перед прокруткой targetElement в видимую область. Если прокрутка запрещена на
 * body (например, targetElement находится в popover), будет выполнена прокрутка только
 * родительских элементов targetElement до, но не включая сам body.
 */
export function scrollIntoViewport(targetElement: Element | null, opts?: ScrollIntoViewportOpts): void {
    if (targetElement && document.contains(targetElement)) {
        const root = document.scrollingElement || document.documentElement;
        const isScrollPrevented = window.getComputedStyle(root).overflow === 'hidden';
        // Если прокрутка в данный момент не запрещена, значит мы не находимся в overlay
        // и overlay не открыт, просто используем element.scrollIntoView чтобы показать элемент в области видимости
        if (!isScrollPrevented) {
            const { left: originalLeft, top: originalTop } = targetElement.getBoundingClientRect();

            // используем scrollIntoView({block: 'nearest'}) вместо .focus для проверки
            // полностью ли элемент находится в области видимости, так как .focus()
            // не вызовет прокрутку, если элемент уже в фокусе, и работает
            // непоследовательно, когда элемент частично вне области видимости по горизонтали vs вертикали
            targetElement?.scrollIntoView?.({ block: 'nearest', behavior: opts?.behavior });
            const { left: newLeft, top: newTop } = targetElement.getBoundingClientRect();
            // Account for sub pixel differences from rounding
            if (Math.abs(originalLeft - newLeft) > 1 || Math.abs(originalTop - newTop) > 1) {
                opts?.containingElement?.scrollIntoView?.({
                    block: 'center',
                    inline: 'center',
                    behavior: opts?.behavior,
                });
                targetElement.scrollIntoView?.({ block: 'nearest', behavior: opts?.behavior });
            }
        } else {
            const scrollParents = getScrollParents(targetElement);
            // Если прокрутка запрещена, мы не хотим прокручивать body, так как это может сдвинуть overlay частично
            // за пределы экрана, и пользователь не сможет прокрутить его обратно в область видимости.
            for (const scrollParent of scrollParents) {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- автоматический игнор в рамках DIRECT-224975
                scrollIntoView(scrollParent as HTMLElement, targetElement as HTMLElement, opts?.behavior);
            }
        }
    }
}
